import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { BasicContainer, FlexCol, H2Text } from '@minecraft.atoms';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { captureExceptionEvent } from '@blocs.observability';
import { WindowWidthContext } from '@minecraft.utils';
import { DiscountTypeEnum } from '@minecraft.graphql-operations';
import { AddressChange } from '@blocs.address-suggestion';
import { applySuggestedAddressToFormik } from '../../AddressValidation/applySuggestedAddress';
import { PaymentFormProps, PaymentMethodFormType } from '../types';
import { usePayPal } from '../../PayPal';
import { PAYMENT_METHOD_TYPE } from '../../enums/PaymentMethods';
import { BillingCodes } from '../../enums/plans';
import { UpgradeModalExperimentGroups } from '../../enums/experimentGroups';
import { useRegistrationLocation } from '../../useRegistrationLocation';
import { usePaywallFunnelEvents } from '../../usePaywallFunnelEvents';
import { priceFormatter } from '../../helpers/priceFormatter';
import { generateUniqueId } from '../../helpers/functions';
import { PlanType } from '../../usePlans';
import { FEATURE_FLAGS, useFeatureExperiment } from '../../../FeaturesAndExperiments';
import { createPaymentMethodValidationSchema } from './ValidationSchema';
import { ExpressPaymentSection } from './ExpressPaymentSection';
import { CreditCardFields } from './CreditCardFields';
import { PaymentPriorityCheckboxes } from './PaymentPriorityCheckboxes';
import { SummarySection } from './SummarySection';
import { WalletCTA } from './WalletCTA';
import { CheckoutCTA } from './CheckoutCTA';

const CheckoutStartedWatcher: FC<{ onFire: () => void }> = ({ onFire }) => {
  const { touched } = useFormikContext();
  const hasTouchedAnyField = Object.keys(touched).length > 0;
  useEffect(() => {
    if (hasTouchedAnyField) {
      onFire();
    }
  }, [hasTouchedAnyField, onFire]);
  return null;
};

export const PaymentForm: FC<PaymentFormProps> = ({
  mode,
  selectedCard,
  wallet,
  selectedPaymentOption,
  showAddPaymentMethod,
  setShowAddPaymentMethod,
  selectedPlan,
  currentPurchase,
  completePurchaseLoading,
  completePurchaseError,
  onApplyCoupon,
  applyCouponError,
  couponLoading,
  onRemoveCoupon,
  purchaseableItems,
  closeBillingPopup,
  modalVariant,
  isModal,
  enableAnnualRenewal,
  allowPurchase,
  requireRegistrationPromoFlag = true,
  forceDecimalPricing = false,
  onSubmit,
  onAddCardDuringCheckout,
  onPayPalToken,
  addPaymentError,
  addPaymentLoading,
  paymentMethodFormRef,
  paymentMethodFormikRef,
  handlePaymentPriorityChange,
  showApplePayButton,
  onClickApplePay,
  threeDActionContext,
  currencyCode,
  countryCode,
  paywallLocation,
  allowCancel,
  onCancel,
  ctaLabel,
  omitTitle,
  showAuthorizeMessage,
  showFormBorder = true,
  ...divProps
}) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const isMobile = WindowWidthContext.useIsMobile();
  const { isRegistration } = useRegistrationLocation({ paywallLocation });
  const [acceptedTrialTerms, setAcceptedTrialTerms] = useState<boolean>(false);
  const isPaywallBillingAddressEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_PAYWALL_BILLING_ADDRESS);
  const allFunnelEvents = usePaywallFunnelEvents(paywallLocation, isModal);
  const funnelEvents = mode === 'checkout' ? allFunnelEvents : undefined;

  const walletCtaDisabled = addPaymentLoading || Boolean(threeDActionContext?.token);

  const billingCode = selectedPlan?.billingCode ?? currentPurchase?.subscription?.plan?.billingCode;
  const isFreePlanSelected = (billingCode as BillingCodes | undefined) === BillingCodes.registration;
  const isCardlessTrial = selectedPlan?.isCardlessTrial;
  const hidePaymentSection = mode === 'checkout' && (isFreePlanSelected || isCardlessTrial);
  // Whether PaymentForm should hide its own card-entry fields — the wallet picker itself is rendered by
  // the caller (see WalletPicker), not here.
  const isBrowsingWallet =
    mode === 'checkout' && !showAddPaymentMethod && !threeDActionContext?.token && !!wallet?.length;
  // Priority (primary/backup) can only be changed while editing an existing saved card — never while
  // adding a new one, in either mode. A newly added card is assigned its priority automatically instead
  // (see getNewPaymentMethodPriority in usePaymentMethodForm.tsx, used by each parent's own submit
  // handler now, not here).
  const showPriorityCheckboxes = mode === 'wallet' && !!selectedCard?.id;
  const isWalletEmpty = !wallet?.length;
  // Shows whenever adding a card during checkout, except for registration starting from an empty wallet.
  const isAddingCardDuringCheckout =
    mode === 'checkout' && !!showAddPaymentMethod && (!isWalletEmpty || !isRegistration);
  // Adding a new card during checkout, alongside an already-populated wallet, is the only case with a
  // "Cancel" path back to the wallet picker — unrelated to whether priority checkboxes are shown.
  const isAddingCardAlongsideWallet = mode === 'checkout' && !!wallet?.length && !!showAddPaymentMethod;
  const isLoading = addPaymentLoading || completePurchaseLoading || couponLoading;
  const isLifestyleUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantB && isModal;
  // Mirrors the conditions guarding <CreditCardFields /> below. The schema must require exactly the
  // fields that are on screen: requiring hidden ones blocks submission on errors the user cannot see,
  // while leaving visible ones optional lets an incomplete form reach Recurly and fail there instead
  // of surfacing an inline error on the offending field.
  const areCardFieldsVisible = !hidePaymentSection && !isBrowsingWallet;
  const validationSchema = useMemo(
    () => createPaymentMethodValidationSchema(t, !!isPaywallBillingAddressEnabled, areCardFieldsVisible),
    [t, isPaywallBillingAddressEnabled, areCardFieldsVisible]
  );

  const price = priceFormatter({ price: currentPurchase?.total ?? 0, currencyCode, countryCode, t });
  const uniqueId = generateUniqueId({ plan: selectedPlan, paywallLocation, customPrefix: 'complete-purchase-button' });

  const getCompletePurchaseCta = (plan?: PlanType) => {
    if (plan?.isTrialPeriodEnabled && !currentPurchase?.coupon?.code) return plan?.cta;
    if (
      plan?.discountType === DiscountTypeEnum.ReppedRegistrationDiscount &&
      (plan?.billingCode as BillingCodes | undefined) === BillingCodes.registration
    )
      return t('common:form.label.getStarted');
    if ((plan?.billingCode as BillingCodes | undefined) === BillingCodes.registration)
      return t('common:paywall.summary.register');
    return t('common:paywall.summary.pay', { price });
  };

  const paymentPriority = [];
  if (mode === 'wallet' && selectedCard?.isPrimaryPaymentMethod) paymentPriority.push('primary');
  if (mode === 'wallet' && selectedCard?.isBackupPaymentMethod) paymentPriority.push('backup');

  const handleFormikSubmit = useCallback(
    (formData: PaymentMethodFormType, formikHelpers: FormikHelpers<PaymentMethodFormType>) =>
      onSubmit({ ...formData, acceptedTrialTerms }, formikHelpers),
    [onSubmit, acceptedTrialTerms]
  );

  const handleTogglePaymentPriority = useCallback(
    (type: PAYMENT_METHOD_TYPE.PRIMARY | PAYMENT_METHOD_TYPE.BACKUP, isChecked: boolean) => {
      const currentPriority = paymentMethodFormikRef.current?.values?.paymentPriority ?? [];
      const newPriority = isChecked
        ? [...currentPriority, type]
        : currentPriority.filter((priority: PAYMENT_METHOD_TYPE) => priority !== type);
      paymentMethodFormikRef.current?.setFieldValue('paymentPriority', newPriority);
      handlePaymentPriorityChange?.({
        billingInfoId: selectedCard?.id?.toString(),
        isPrimaryPaymentMethod: type === PAYMENT_METHOD_TYPE.PRIMARY ? isChecked : undefined,
        isBackupPaymentMethod: type === PAYMENT_METHOD_TYPE.BACKUP ? isChecked : undefined,
      });
    },
    [paymentMethodFormikRef, handlePaymentPriorityChange, selectedCard?.id]
  );

  const handleAddressLine1Change = useCallback(
    (change: AddressChange) => {
      const formik = paymentMethodFormikRef.current;

      if (!formik) return;

      if (change.line1Only === true) {
        formik.setFieldValue('address_line_1', change.address.address1);
        return;
      }

      funnelEvents?.trackBillingAddressAutofillUsed();
      applySuggestedAddressToFormik(formik, change.address);
    },
    [paymentMethodFormikRef, funnelEvents]
  );

  const { onClick: onClickPayPal, showPayPal } = usePayPal({
    onToken: async ({ token }) => {
      try {
        await onPayPalToken?.(token);
      } catch (error) {
        console.error('onAddPaymentOption error', error);
        throw error;
      }
    },
    onError: ({ error }) => {
      captureExceptionEvent(error, { location: 'useAddPaymentMethods > add PayPal' });
      showMessage({ color: 'danger', message: t('common:globalError.generic') });
    },
  });

  return (
    <BasicContainer
      data-testid="add-payment-container"
      {...(mode === 'wallet' && { padding: 4, borderRadius: 'md' })}
      {...(mode === 'wallet' && !isFreePlanSelected && showFormBorder && { borderPlacement: 'all' })}
      {...divProps}
    >
      <FlexCol gap="4">
        {!omitTitle && mode === 'wallet' && (
          <H2Text>
            {selectedCard?.id ? t('common:billing.card.edit') : t('common:paywall.addPaymentMethod.title')}
          </H2Text>
        )}
        <Formik
          key={mode === 'wallet' ? `${selectedCard?.id}-${paymentPriority.join()}` : undefined}
          innerRef={paymentMethodFormikRef}
          validationSchema={validationSchema}
          initialValues={{
            billingInfoId: selectedCard?.id?.toString(),
            // eslint-disable-next-line camelcase
            first_name: selectedCard?.firstName ?? '',
            // eslint-disable-next-line camelcase
            last_name: selectedCard?.lastName ?? '',
            // eslint-disable-next-line camelcase
            postal_code: selectedCard?.postalCode ?? '',
            paymentPriority,
            ...(isPaywallBillingAddressEnabled && {
              // eslint-disable-next-line camelcase
              address_line_1: selectedCard?.street1 ?? '',
              // eslint-disable-next-line camelcase
              address_line_2: selectedCard?.street2 ?? '',
              city: selectedCard?.city ?? '',
              // eslint-disable-next-line camelcase
              state_region: selectedCard?.region ?? '',
              country: selectedCard?.country ?? countryCode ?? '',
            }),
          }}
          onSubmit={handleFormikSubmit}
        >
          <Form ref={paymentMethodFormRef} className="cn_w-full">
            {funnelEvents && <CheckoutStartedWatcher onFire={funnelEvents.trackCheckoutStarted} />}
            <FlexCol gap="4">
              <FlexCol
                gap="6"
                maxW={mode === 'checkout' ? '21' : undefined}
                w="full"
                {...(mode !== 'wallet' &&
                  !isFreePlanSelected &&
                  !isBrowsingWallet &&
                  showFormBorder && { borderPlacement: 'all', borderRadius: 'md', padding: '4' })}
              >
                {!hidePaymentSection && !isBrowsingWallet && (
                  <>
                    {showPriorityCheckboxes && <PaymentPriorityCheckboxes onToggle={handleTogglePaymentPriority} />}
                    {mode === 'checkout' && showAddPaymentMethod && (
                      <H2Text>{t('common:paywall.addPaymentMethod.title')}</H2Text>
                    )}
                    {(mode === 'checkout' || !selectedCard?.id) && (
                      <ExpressPaymentSection
                        showApplePayButton={showApplePayButton}
                        onClickApplePay={onClickApplePay}
                        showPayPal={showPayPal}
                        onClickPayPal={onClickPayPal}
                      />
                    )}
                    <CreditCardFields
                      isMobile={isMobile}
                      selectedCard={selectedCard}
                      isPaywallBillingAddressEnabled={isPaywallBillingAddressEnabled}
                      onAddressLine1Change={handleAddressLine1Change}
                      funnelEvents={funnelEvents}
                    />
                    {isAddingCardDuringCheckout && (
                      <WalletCTA
                        allowCancel={isAddingCardAlongsideWallet}
                        onCancel={() => setShowAddPaymentMethod?.(false)}
                        ctaLabel={t('common:paywall.addPaymentMethod.title')}
                        onSubmitClick={() =>
                          onAddCardDuringCheckout?.(
                            paymentMethodFormikRef.current?.values,
                            paymentMethodFormikRef.current
                          )
                        }
                        disabled={walletCtaDisabled}
                        paywallLocation={paywallLocation}
                      />
                    )}
                  </>
                )}
              </FlexCol>
              <FlexCol gap="3">
                {mode === 'checkout' && (
                  <SummarySection
                    selectedPlan={selectedPlan}
                    currentPurchase={currentPurchase}
                    currencyCode={currencyCode}
                    countryCode={countryCode}
                    onApplyCoupon={onApplyCoupon}
                    applyCouponError={applyCouponError}
                    couponLoading={couponLoading}
                    onRemoveCoupon={onRemoveCoupon}
                    addPaymentLoading={addPaymentLoading}
                    completePurchaseLoading={completePurchaseLoading}
                    purchaseableItems={purchaseableItems}
                    enableAnnualRenewal={enableAnnualRenewal}
                    requireRegistrationPromoFlag={requireRegistrationPromoFlag}
                    forceDecimalPricing={forceDecimalPricing}
                  />
                )}
                {mode === 'wallet' ? (
                  <WalletCTA
                    allowCancel={allowCancel}
                    onCancel={onCancel}
                    ctaLabel={ctaLabel}
                    showAuthorizeMessage={showAuthorizeMessage}
                    addPaymentError={addPaymentError}
                    disabled={walletCtaDisabled}
                    paywallLocation={paywallLocation}
                    threeDActionContext={threeDActionContext}
                  />
                ) : (
                  <CheckoutCTA
                    ctaLabel={getCompletePurchaseCta(selectedPlan)}
                    disabled={allowPurchase === false}
                    isPaywallBillingAddressEnabled={isPaywallBillingAddressEnabled}
                    isLoading={isLoading}
                    selectedPaymentOption={selectedPaymentOption}
                    acceptedTrialTerms={acceptedTrialTerms}
                    setAcceptedTrialTerms={setAcceptedTrialTerms}
                    selectedPlan={selectedPlan}
                    currentPurchase={currentPurchase}
                    countryCode={countryCode}
                    isMobile={isMobile}
                    isRegistration={isRegistration}
                    isLifestyleUpgradeModal={isLifestyleUpgradeModal}
                    purchaseableItems={purchaseableItems}
                    closeBillingPopup={closeBillingPopup}
                    completePurchaseError={completePurchaseError}
                    addPaymentError={addPaymentError}
                    threeDActionContext={threeDActionContext}
                    uniqueId={uniqueId}
                    funnelEvents={funnelEvents}
                  />
                )}
              </FlexCol>
            </FlexCol>
          </Form>
        </Formik>
      </FlexCol>
    </BasicContainer>
  );
};
